import clsx from 'clsx';
import {
  ProjectJobRoleFragmentDoc,
  useJobRoleDuplicateMutation,
  useJobRoleUpdateOneMutation,
  useProjectJobRoleCreateOneMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  Modal,
  ModalSize,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  buildDuplicationQualifier,
  TDuplicableJobRole,
  useGetDuplicableJobRoles,
} from './DuplicateJobRoleModal.helpers';
import DuplicateJobRolePreview from './DuplicateJobRolePreview';
import DuplicateJobRoleResults from './DuplicateJobRoleResults';
import styles from './DuplicateJobRoleModal.module.scss';

export type TDuplicateJobRoleModalProps = {
  onClose: () => void;
  projectId: number;
  companyId: number;
};

export const dataTestIds = {
  back: 'duplicate-job-role-modal__back',
  cancel: 'duplicate-job-role-modal__cancel',
  duplicate: 'duplicate-job-role-modal__duplicate',
};

export function DuplicateJobRoleModal({
  onClose,
  projectId,
  companyId,
}: TDuplicateJobRoleModalProps) {
  return (
    <Modal
      isOpen
      onClose={onClose}
      modalSize={ModalSize.MEDIUM}
      className="duplicate-job-role-modal"
      header={
        <div className={styles.header}>
          <span className={styles.title}>Duplicate a Role Skills Profile</span>
        </div>
      }
    >
      <Content onClose={onClose} projectId={projectId} companyId={companyId} />
    </Modal>
  );
}

type ContentProps = {
  projectId: number;
  companyId: number;
  onClose: () => void;
};

const Content = ({ projectId, companyId, onClose }: ContentProps) => {
  const { handleMsgType } = useNotification();

  const [step, setStep] = useState<1 | 2>(1);
  const [selectedJobRole, setSelectedJobRole] = useState<TDuplicableJobRole>();
  const [name, setName] = useState('');
  const [qualifier, setQualifier] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    jobRoles,
    loading,
    count,
    currentlyActiveJobRolesLevel,
    activeLevelLabel,
    hasMore,
    searchValue,
    debouncedSearch,
    loadMore,
    appliedFilters,
    applyFilters,
    activeFilterCount,
    itemsTotal,
  } = useGetDuplicableJobRoles({ companyId, projectId });

  const [jobRoleDuplicate] = useJobRoleDuplicateMutation();
  const [jobRoleUpdateOne] = useJobRoleUpdateOneMutation();
  const [projectJobRoleCreateOne] = useProjectJobRoleCreateOneMutation({
    update: (cache, { data }) => {
      const newProjectJobRole = data?.ProjectJobRoleCreateOne;
      if (!newProjectJobRole) return;

      cache.modify({
        fields: {
          ProjectJobRoleFindMany(existingProjectJobRoles = []) {
            const newProjectJobRoleRef = cache.writeFragment({
              data: newProjectJobRole,
              fragment: ProjectJobRoleFragmentDoc,
              fragmentName: 'ProjectJobRole',
            });

            return [...existingProjectJobRoles, newProjectJobRoleRef];
          },
        },
      });
    },
  });

  const handleSelect = (role: TDuplicableJobRole) => {
    setSelectedJobRole(role);
    setName(role.name);
    setQualifier(buildDuplicationQualifier(role));
    setStep(2);
  };

  async function handleConfirm() {
    if (isSubmitting || !selectedJobRole) return;
    setIsSubmitting(true);

    let duplicatedRoleCreated = false;
    try {
      const { data } = await jobRoleDuplicate({
        variables: { id: selectedJobRole.id },
      });

      const newRole = data?.JobRoleDuplicate;
      const newRoleId = newRole?.id;
      const newSkillsProfileId = newRole?.skillsProfiles[0]?.id;

      if (!newRoleId || !newSkillsProfileId) {
        handleMsgType({
          type: TNotification.error,
          message:
            "The selected Role Skills Profile has no skills profile and can't be duplicated.",
        });
        return;
      }

      duplicatedRoleCreated = true;

      // Set the (possibly edited) name and the provenance note. The note is the
      // role's `qualifier` (surfaced to admins as "additional information"), which
      // prefills to the default copy but can be edited/appended in step 2.
      await jobRoleUpdateOne({
        variables: {
          id: newRoleId,
          name: name.trim() || selectedJobRole.name,
          qualifier: qualifier.trim() || buildDuplicationQualifier(selectedJobRole),
        },
      });

      await projectJobRoleCreateOne({
        variables: {
          jobRoleId: newRoleId,
          projectId,
          skillsProfileId: newSkillsProfileId,
        },
      });

      handleMsgType({
        type: TNotification.success,
        message: 'Role Skills Profile duplicated successfully',
      });
      onClose();
    } catch (error) {
      handleMsgType({
        type: TNotification.error,
        message: duplicatedRoleCreated
          ? 'Role Skills Profile was duplicated but could not be added to the role. Please try adding it from the list.'
          : (error as Error)?.message ||
            'Oops, some issue happened while duplicating the Role Skills Profile',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const steps = [
    { glyph: step >= 2 ? '✓' : '1', label: 'Find a role', active: true },
    { glyph: '2', label: 'Review & name', active: step >= 2 },
  ];

  return (
    <div className={styles.content}>
      <div className={styles.stepper}>
        {steps.map((s) => (
          <div key={s.label} className={styles.step}>
            <span
              className={clsx(
                styles.stepCircle,
                s.active ? styles.stepCircleActive : styles.stepCircleIdle,
              )}
            >
              {s.glyph}
            </span>
            <span
              className={clsx(
                styles.stepLabel,
                s.active && styles.stepLabelActive,
              )}
            >
              {s.label}
            </span>
          </div>
        ))}
        <span className={styles.stepperLine} />
      </div>

      <div className={styles.body}>
        {step === 1 ? (
          <DuplicateJobRoleResults
            jobRoles={jobRoles}
            loading={loading}
            count={count}
            selectedId={selectedJobRole?.id}
            onSelect={handleSelect}
            searchValue={searchValue}
            onSearch={debouncedSearch}
            hasMore={hasMore}
            onLoadMore={loadMore}
            appliedFilters={appliedFilters}
            onApplyFilters={applyFilters}
            activeFilterCount={activeFilterCount}
            lockedRoleLevel={currentlyActiveJobRolesLevel}
            activeLevelLabel={activeLevelLabel}
            itemsTotal={itemsTotal}
          />
        ) : (
          selectedJobRole && (
            <div className={styles.step2}>
              <button
                type="button"
                className={styles.backButton}
                onClick={() => setStep(1)}
                data-testid={dataTestIds.back}
              >
                ‹ Back to search
              </button>
              <DuplicateJobRolePreview
                jobRole={selectedJobRole}
                name={name}
                onNameChange={setName}
                qualifier={qualifier}
                onQualifierChange={setQualifier}
              />
            </div>
          )
        )}
      </div>

      <div className={styles.footer}>
        <span className={styles.footerNote}>
          {step === 2
            ? 'Duplicated Role Skills Profiles have a "Admin app" source.'
            : 'Step 1 of 2 · pick a Role Skills Profile to duplicate'}
        </span>
        <div className={styles.footerButtons}>
          <Button
            variant="secondary"
            size="medium"
            data-testid={dataTestIds.cancel}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {step === 2 && (
            <Button
              variant="primary"
              size="medium"
              data-testid={dataTestIds.duplicate}
              onClick={handleConfirm}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Duplicating…' : 'Duplicate'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
